/**
 * "Create routes" — the Easy button (Part A of
 * memory-bank/plans/route-autoplan-and-knock-map.md).
 *
 * A coordinator who has never seen this tool should be able to open it, read
 * one sentence, and press Create. So the modal opens with a plan ALREADY
 * previewed from server defaults and every knob folded away behind "More
 * options". Nothing has to be filled in.
 *
 * Two clicks, and only the second one writes. That is the safety, not a
 * ceremony: routes are effectively write-once — PATCH touches only
 * name/status/google_maps_url, stops cannot be reordered or removed, and there
 * is no DELETE. Archiving is the only retreat, so a 100-route misclick is
 * permanent clutter.
 *
 * ⚠️ The exclusion counts below are the reason this modal is not just a button.
 * A district that produces three routes instead of thirty is nearly always
 * saying "your roll is not geocoded" or "you already planned these doors" —
 * and a UI that shows only the route count leaves the coordinator to guess,
 * which is the same dead-honesty failure as the lasso footer's "0 missing
 * coordinates". Every door in the reach is accounted for on screen.
 *
 * Styling follows CreateRouteModal's idiom (semantic tokens, right-side panel).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ChevronRight,
  MapPin,
} from 'lucide-react';
import {
  previewAutoplan,
  commitAutoplan,
  type AutoplanRequest,
  type AutoplanPreviewResponse,
} from '@/api/routes';

/** Mirrors the server ceiling; used to explain, never to gate — the server decides. */
const MAX_ROUTES = 250;

type Canvasser = { id: number; full_name: string };

type Props = {
  onClose: () => void;
  /** Called after a successful commit so the list can refresh. */
  onCreated: (routeCount: number, shiftCount: number) => void;
  /**
   * Canvassers available for shift assignment. Empty is fine and common —
   * the shifts section then explains why it is unavailable rather than
   * rendering an empty picker.
   */
  canvassers?: Canvasser[];
  /**
   * Restrict the plan to these precincts from the moment the modal opens.
   *
   * This is how "Cover the gaps" (easy-buttons.md §3 #2) composes: Coverage
   * works out which precincts are worst covered and hands them here, so the
   * same planner, the same preview and the same commit do the work. No second
   * planner, no new endpoint.
   */
  initialPrecincts?: string[];
  /**
   * One line saying where a pre-filled restriction came from, rendered above
   * the plan. A caller that passes `initialPrecincts` should pass this too:
   * a plan that quietly covers five precincts instead of the district is the
   * kind of thing a coordinator finds out about on Saturday morning.
   */
  originNote?: React.ReactNode;
};

function extractError(err: unknown, fallback: string): string {
  const resp = (err as any)?.response;
  if (resp?.status === 403) {
    return (
      resp?.data?.detail ||
      'You do not have permission to create routes. This requires a coordinator or above.'
    );
  }
  const detail = resp?.data?.detail;
  if (Array.isArray(detail)) {
    // Pydantic validation errors arrive as a list of objects; joining the
    // messages beats rendering "[object Object]" at a coordinator.
    return detail.map((d: any) => d?.msg).filter(Boolean).join('; ') || fallback;
  }
  return detail || (err as any)?.message || fallback;
}

function isAbortError(err: any): boolean {
  return (
    err?.name === 'CanceledError' ||
    err?.name === 'AbortError' ||
    err?.code === 'ERR_CANCELED'
  );
}

const n = (v: number) => v.toLocaleString();

export default function AutoplanRoutesModal({
  onClose,
  onCreated,
  canvassers = [],
  initialPrecincts,
  originNote,
}: Props) {
  const prefilled = (initialPrecincts?.length ?? 0) > 0;

  // Server defaults, restated here only so the inputs have something to show.
  // The request omits anything the coordinator did not touch.
  const [maxRoutes, setMaxRoutes] = useState(100);
  const [maxDoors, setMaxDoors] = useState(12);
  const [skipRouted, setSkipRouted] = useState(true);
  const [namePrefix, setNamePrefix] = useState('');
  const [precincts, setPrecincts] = useState(
    initialPrecincts?.join(', ') ?? '',
  );
  const [minScore, setMinScore] = useState('');
  // Collapsed by default is the whole point of this modal — except when a
  // caller pre-filled the precinct list. A restriction the coordinator cannot
  // see is a hidden filter, and it is also the one field they are most likely
  // to want to widen, so open the drawer on the precincts they were handed.
  const [showOptions, setShowOptions] = useState(prefilled);

  const [createShifts, setCreateShifts] = useState(false);
  const [shiftDate, setShiftDate] = useState('');
  const [assignees, setAssignees] = useState<number[]>([]);

  const [preview, setPreview] = useState<AutoplanPreviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputCls =
    'w-full rounded-md border border-border-subtle bg-bg-base px-3 py-2 text-sm text-fg-primary placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-accent';

  /**
   * The request body, built once and used by BOTH preview and commit.
   *
   * Commit re-plans from this body rather than posting groups back, so the two
   * calls must send byte-identical params — a preview that plans differently
   * than the commit is a lie in the one place the coordinator is trusting the
   * numbers. One memo, two callers, no chance to drift.
   */
  const params = useMemo<AutoplanRequest>(() => {
    const precinctList = precincts
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    const score = minScore.trim() === '' ? null : Number(minScore);
    return {
      max_routes: maxRoutes,
      max_doors_per_route: maxDoors,
      skip_already_routed: skipRouted,
      name_prefix: namePrefix.trim() || null,
      precinct_nums: precinctList.length ? precinctList : null,
      min_score: score !== null && Number.isFinite(score) ? score : null,
    };
  }, [maxRoutes, maxDoors, skipRouted, namePrefix, precincts, minScore]);

  // Preview on open and on every knob change, debounced — typing a door count
  // would otherwise fire a request per keystroke.
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const t = setTimeout(() => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      setError(null);
      previewAutoplan(params, ctrl.signal)
        .then((data) => {
          setPreview(data);
          setLoading(false);
        })
        .catch((err) => {
          if (isAbortError(err)) return;
          setPreview(null);
          setError(extractError(err, 'Could not work out a plan for your district.'));
          setLoading(false);
        });
    }, 300);
    return () => clearTimeout(t);
  }, [params]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const shiftsIncomplete = createShifts && (!shiftDate || assignees.length === 0);

  const handleCommit = useCallback(async () => {
    if (!preview || preview.route_count === 0) return;
    setCommitting(true);
    setError(null);
    try {
      const body: AutoplanRequest = { ...params };
      if (createShifts) {
        body.create_shifts = true;
        // The server wants a datetime; a bare date input gives "2026-09-05".
        body.shift_date = shiftDate.includes('T') ? shiftDate : `${shiftDate}T09:00:00`;
        body.assign_to_user_ids = assignees;
      }
      const res = await commitAutoplan(body);
      onCreated(res.routes.length, res.shifts_created);
    } catch (err) {
      setError(extractError(err, 'Could not create the routes. Nothing was written.'));
      setCommitting(false);
    }
  }, [preview, params, createShifts, shiftDate, assignees, onCreated]);

  /**
   * The exclusions worth putting in front of someone, with what to DO about
   * each. Only non-zero ones render — a wall of zeroes is noise, and it would
   * bury the one line that matters.
   */
  const exclusions = useMemo(() => {
    if (!preview) return [];
    const out: Array<{ label: string; count: number; hint: string }> = [];
    if (preview.doors_excluded_no_coords > 0) {
      out.push({
        label: 'not geocoded',
        count: preview.doors_excluded_no_coords,
        hint: 'These have no map coordinates, so they cannot be put in walking order. Run the geocode backfill to reach them.',
      });
    }
    if (preview.doors_excluded_already_routed > 0) {
      out.push({
        label: 'already on a route',
        count: preview.doors_excluded_already_routed,
        hint: 'Skipped so nobody knocks the same door twice. Press Create routes again after these are walked to plan the next batch.',
      });
    }
    if (preview.doors_excluded_by_min_score > 0) {
      out.push({
        label: 'below your score floor',
        count: preview.doors_excluded_by_min_score,
        hint: 'Excluded by the minimum score under More options.',
      });
    }
    return out;
  }, [preview]);

  const empty = !!preview && preview.route_count === 0;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 flex h-full w-full max-w-lg flex-col bg-bg-raised shadow-xl">
        <div className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
          <h2 className="text-base font-semibold text-fg-primary">Create routes</h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-fg-muted hover:bg-bg-base hover:text-fg-primary"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
          <p className="text-sm text-fg-secondary">
            {/* Reads the live field, not the prop: clear the precinct list and
                the plan really does widen to the whole district, so the
                sentence has to widen with it. */}
            {precincts.trim()
              ? 'We will lay out walking routes in the precincts listed below, densest first. Nothing is created until you press Create.'
              : 'We will lay out walking routes across your district, densest precincts first. Nothing is created until you press Create.'}
          </p>

          {originNote && (
            <div className="rounded-md border border-border-subtle bg-bg-base px-3 py-2 text-xs text-fg-secondary">
              {originNote}
            </div>
          )}

          {loading && (
            <div className="flex items-center gap-2 py-6 text-sm text-fg-muted">
              <Loader2 size={16} className="animate-spin" />
              Planning your routes…
            </div>
          )}

          {/* status-danger, not status-error: only the former is a real token,
              so the -error spelling rendered an uncoloured box. */}
          {error && (
            <div className="rounded-md border border-status-danger/40 bg-status-danger/10 px-3 py-2 text-xs text-status-danger">
              {error}
            </div>
          )}

          {preview && !loading && (
            <>
              {/* ---- The headline ---- */}
              {empty ? (
                <div className="flex gap-2 rounded-md border border-status-warning/40 bg-status-warning/10 px-3 py-3 text-sm text-fg-primary">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0 text-status-warning" />
                  <span>
                    No routes to plan right now. Every door in your district is
                    either already on a route or missing map coordinates — see
                    the breakdown below.
                  </span>
                </div>
              ) : (
                <div className="rounded-md border border-border-subtle bg-bg-base px-4 py-3">
                  <div className="text-2xl font-semibold text-fg-primary">
                    {n(preview.route_count)}{' '}
                    <span className="text-base font-normal text-fg-secondary">
                      {preview.route_count === 1 ? 'route' : 'routes'}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-fg-secondary">
                    {n(preview.doors_planned)} doors across{' '}
                    {preview.precincts_planned.length}{' '}
                    {preview.precincts_planned.length === 1 ? 'precinct' : 'precincts'} —{' '}
                    {preview.coverage_pct}% of the {n(preview.doors_in_reach)} in your
                    district.
                  </div>
                </div>
              )}

              {/* ---- What was left out, and what to do about it ---- */}
              {exclusions.length > 0 && (
                <div className="rounded-md border border-border-subtle bg-bg-base px-3 py-2">
                  <div className="mb-1 text-xs font-semibold text-fg-secondary">
                    Doors not included
                  </div>
                  <ul className="flex flex-col gap-2">
                    {exclusions.map((e) => (
                      <li key={e.label} className="text-xs text-fg-muted">
                        <span className="font-semibold text-fg-primary">
                          {n(e.count)}
                        </span>{' '}
                        {e.label} — {e.hint}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {preview.routes_capped && !empty && (
                <div className="flex gap-2 rounded-md border border-status-warning/40 bg-status-warning/10 px-3 py-2 text-xs text-fg-primary">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0 text-status-warning" />
                  <span>
                    This is a batch, not your whole district —{' '}
                    <strong>{n(preview.doors_not_planned)}</strong> more doors are
                    ready to plan. Press Create routes again once these are walked,
                    or raise the route limit under More options (max {MAX_ROUTES}).
                  </span>
                </div>
              )}

              {/* ---- The plan itself ---- */}
              {!empty && (
                <details className="rounded-md border border-border-subtle bg-bg-base">
                  <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-fg-secondary">
                    Show the {n(preview.route_count)}{' '}
                    {preview.route_count === 1 ? 'route' : 'routes'}
                  </summary>
                  <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto border-t border-border-subtle px-3 py-2">
                    {preview.groups.map((g, i) => (
                      <li key={i} className="flex items-baseline gap-2 text-xs">
                        <MapPin size={12} className="shrink-0 text-fg-muted" />
                        <span className="min-w-0 flex-1 truncate text-fg-primary">
                          {g.name}
                        </span>
                        <span className="shrink-0 text-fg-muted">
                          {g.door_count} doors · ~{g.est_hours.toFixed(1)}h
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}

          {/* ---- More options: collapsed by default, ON PURPOSE ---- */}
          <div className="rounded-md border border-border-subtle">
            <button
              type="button"
              onClick={() => setShowOptions((v) => !v)}
              className="flex w-full items-center gap-1 px-3 py-2 text-xs font-semibold text-fg-secondary hover:text-fg-primary"
              aria-expanded={showOptions}
            >
              {showOptions ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              More options
            </button>

            {showOptions && (
              <div className="flex flex-col gap-3 border-t border-border-subtle px-3 py-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor="ap-max-routes"
                      className="mb-1 block text-xs font-semibold text-fg-secondary"
                    >
                      Max routes
                    </label>
                    <input
                      id="ap-max-routes"
                      type="number"
                      min={1}
                      max={MAX_ROUTES}
                      value={maxRoutes}
                      onChange={(e) => setMaxRoutes(Number(e.target.value))}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="ap-max-doors"
                      className="mb-1 block text-xs font-semibold text-fg-secondary"
                    >
                      Doors per route
                    </label>
                    <input
                      id="ap-max-doors"
                      type="number"
                      min={5}
                      max={500}
                      value={maxDoors}
                      onChange={(e) => setMaxDoors(Number(e.target.value))}
                      className={inputCls}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor="ap-precincts"
                      className="mb-1 block text-xs font-semibold text-fg-secondary"
                    >
                      Precincts
                    </label>
                    <input
                      id="ap-precincts"
                      type="text"
                      value={precincts}
                      placeholder="All — or 12, 15"
                      onChange={(e) => setPrecincts(e.target.value)}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="ap-min-score"
                      className="mb-1 block text-xs font-semibold text-fg-secondary"
                    >
                      Minimum score
                    </label>
                    <input
                      id="ap-min-score"
                      type="number"
                      min={0}
                      max={100}
                      value={minScore}
                      placeholder="No floor"
                      onChange={(e) => setMinScore(e.target.value)}
                      className={inputCls}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="ap-prefix"
                    className="mb-1 block text-xs font-semibold text-fg-secondary"
                  >
                    Name prefix
                  </label>
                  <input
                    id="ap-prefix"
                    type="text"
                    value={namePrefix}
                    placeholder="Your candidate's name"
                    onChange={(e) => setNamePrefix(e.target.value)}
                    className={inputCls}
                  />
                </div>

                <label className="flex items-start gap-2 text-xs text-fg-secondary">
                  <input
                    type="checkbox"
                    checked={skipRouted}
                    onChange={(e) => setSkipRouted(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    Skip doors that are already on a route
                    <span className="block text-fg-muted">
                      Leave this on. Turning it off can put the same house on two
                      routes, and two volunteers will knock it.
                    </span>
                  </span>
                </label>
              </div>
            )}
          </div>

          {/* ---- Shifts: off by default. Most people don't care. ---- */}
          <div className="rounded-md border border-border-subtle px-3 py-3">
            <label className="flex items-start gap-2 text-xs text-fg-secondary">
              <input
                type="checkbox"
                checked={createShifts}
                onChange={(e) => setCreateShifts(e.target.checked)}
                className="mt-0.5"
                disabled={canvassers.length === 0}
              />
              <span>
                <span className="font-semibold text-fg-primary">
                  Also schedule these as shifts
                </span>
                <span className="block text-fg-muted">
                  {canvassers.length === 0
                    ? 'No canvassers in this campaign yet — invite one first. You can always assign routes later.'
                    : 'Optional. Without this you just get routes, and you can hand them out whenever.'}
                </span>
              </span>
            </label>

            {createShifts && canvassers.length > 0 && (
              <div className="mt-3 flex flex-col gap-3">
                <div>
                  <label
                    htmlFor="ap-shift-date"
                    className="mb-1 block text-xs font-semibold text-fg-secondary"
                  >
                    Date
                  </label>
                  <input
                    id="ap-shift-date"
                    type="date"
                    value={shiftDate}
                    onChange={(e) => setShiftDate(e.target.value)}
                    className={inputCls}
                  />
                </div>
                <div>
                  <div className="mb-1 text-xs font-semibold text-fg-secondary">
                    Canvassers ({assignees.length} selected)
                  </div>
                  <ul className="flex max-h-40 flex-col gap-1 overflow-y-auto rounded-md border border-border-subtle bg-bg-base px-2 py-2">
                    {canvassers.map((c) => (
                      <li key={c.id}>
                        <label className="flex items-center gap-2 text-xs text-fg-primary">
                          <input
                            type="checkbox"
                            checked={assignees.includes(c.id)}
                            onChange={(e) =>
                              setAssignees((prev) =>
                                e.target.checked
                                  ? [...prev, c.id]
                                  : prev.filter((id) => id !== c.id),
                              )
                            }
                          />
                          {c.full_name}
                        </label>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1 text-xs text-fg-muted">
                    Routes are shared out evenly, in the order listed.
                  </p>
                </div>
              </div>
            )}
          </div>

          {preview && !loading && !empty && (
            <p className="text-xs text-fg-muted">
              Routes are created as drafts, so you can look them over before
              anyone walks them. Hours are estimated from door counts.
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border-subtle px-5 py-4">
          <button
            onClick={onClose}
            className="rounded-md px-3 py-2 text-sm text-fg-secondary hover:bg-bg-base"
          >
            Cancel
          </button>
          <button
            onClick={handleCommit}
            disabled={!preview || loading || committing || empty || shiftsIncomplete}
            title={
              shiftsIncomplete
                ? 'Pick a date and at least one canvasser, or turn off shifts.'
                : undefined
            }
            className="inline-flex items-center gap-2 rounded-md bg-accent px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {committing && <Loader2 size={14} className="animate-spin" />}
            {committing
              ? 'Creating…'
              : preview && !empty
                ? `Create ${n(preview.route_count)} ${
                    preview.route_count === 1 ? 'route' : 'routes'
                  }`
                : 'Create routes'}
          </button>
        </div>
      </div>
    </div>
  );
}
